import { BOOKING_STATUSES, STATUS_META, type BookingStatus } from "@/lib/bookings";

interface BookingsToolbarProps {
  search: string;
  onSearch: (value: string) => void;
  statusFilter: BookingStatus | "all";
  onStatusFilter: (value: BookingStatus | "all") => void;
  typeFilter: string;
  onTypeFilter: (value: string) => void;
  resultCount: number;
  onRefresh: () => void;
  refreshing: boolean;
}

const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "experience", label: "Experiences" },
  { value: "trip", label: "Itineraries" },
  { value: "farm", label: "Farms" },
  { value: "farmer", label: "Farmers" },
];

export default function BookingsToolbar({
  search,
  onSearch,
  statusFilter,
  onStatusFilter,
  typeFilter,
  onTypeFilter,
  resultCount,
  onRefresh,
  refreshing,
}: BookingsToolbarProps) {
  const statusOptions: { value: BookingStatus | "all"; label: string }[] = [
    { value: "all", label: "All" },
    ...BOOKING_STATUSES.map((status) => ({ value: status, label: STATUS_META[status].label })),
  ];

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="relative flex-1 min-w-0">
          <span className="w-5 h-5 flex items-center justify-center absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search by name, email, experience…"
            className="w-full bg-background-100 border border-background-300 rounded-md pl-11 pr-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
          />
        </div>

        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          <select
            value={typeFilter}
            onChange={(event) => onTypeFilter(event.target.value)}
            className="bg-background-100 border border-background-300 rounded-md px-3 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
          >
            {TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-3 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
          >
            <i className={`ri-refresh-line ${refreshing ? "animate-spin" : ""}`}></i>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1 overflow-x-auto">
          {statusOptions.map((option) => {
            const active = statusFilter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onStatusFilter(option.value)}
                className={`text-xs md:text-sm font-semibold px-3.5 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs md:text-sm text-foreground-500 ml-auto whitespace-nowrap">
          {resultCount} {resultCount === 1 ? "booking" : "bookings"}
        </p>
      </div>
    </div>
  );
}